/* eslint-disable no-unused-vars */
import { useRef, useId, useEffect } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { publisherReportsFiltersTableActions } from 'store/actions';
import styled, { css } from 'styled-components';
import { Palette } from 'config';
import { useSignal, useComputed } from '@preact/signals-react';
import { useRerender } from 'utils';
import { Search, Each, Buttons, ParagraphWithTooltip, MenuItem, CheckboxInner } from 'components';
import { Search as SmallSearchIcon } from 'assets/icons';
import ReactCountryFlag from 'react-country-flag';

const getIcon = () => {
  return <SmallSearchIcon />;
};

export const Country = ({ countryList$ }) => {
  const initialCountriesFilter = useSelector(
    (state) => state.publisherReportsFiltersTable.countriesFilter,
  );

  const dispatch = useDispatch();
  const rerender = useRerender();
  const searchRef = useRef(null);
  const searchValue$ = useSignal('');
  const changingFlag$ = useSignal(false);

  const countriesFilter$ = useSignal(new Set(initialCountriesFilter));

  const searchId = useId();
  const resetBtnId = useId();
  const applyBtnId = useId();

  const filteredCountryList$ = useComputed(() => {
    const res = Array.from(countryList$.value.values()).filter(({ country, country_code }) => {
      if (country.toLowerCase().includes(searchValue$.value.toLowerCase())) return true;
      if (country_code.toLowerCase().includes(searchValue$.value.toLowerCase())) return true;
      return false;
    });
    return res;
  });

  const sortedCountryList$ = useComputed(() => {
    const filteredCountries = filteredCountryList$.value;
    const inSet = [];
    const notInSet = [];
    filteredCountries.forEach((country) => {
      if (countriesFilter$.value.has(country.country_code)) {
        inSet.push(country);
      } else {
        notInSet.push(country);
      }
    });

    inSet.sort((a, b) => a.country.localeCompare(b.country));
    notInSet.sort((a, b) => a.country.localeCompare(b.country));
    return [...inSet, ...notInSet];
  });

  const handleItemClick = (country_code) => {
    countriesFilter$.value.has(country_code)
      ? countriesFilter$.value.delete(country_code)
      : countriesFilter$.value.add(country_code);
    changingFlag$.value = true;
    rerender();
  };

  const applyClick = () => {
    dispatch(
      publisherReportsFiltersTableActions.updateProperty({
        newValue: Array.from(countriesFilter$.value),
        propField: 'countriesFilter',
      }),
    );
    changingFlag$.value = false;
  };

  const resetClick = () => {
    changingFlag$.value = true;
    countriesFilter$.value.clear();
    rerender();
  };

  return (
    <Box>
      <SearchBox>
        <Search
          onFocus={() => {}}
          ref={searchRef}
          id={searchId}
          onChange={(e) => (searchValue$.value = e.target.value)}
          value={searchValue$?.value}
          placeholder='Search Country'
          rightIconOnClick={(event) => {
            searchValue$.value = '';
          }}
          style={{ height: '48px' }}
          Icon={getIcon}
        />
      </SearchBox>
      <Content>
        <Each
          fieldOfIndex='country_code'
          of={sortedCountryList$.value}
          render={({ country, country_code, pba_numbers }, index) => {
            const my_country_code = country_code === 'UK' ? 'GB' : country_code;
            return (
              <MenuItem
                wrapStyle={{ width: '100%' }}
                isSelected={false}
                height={48}
                value={country_code}
                key={country_code}
                onClick={handleItemClick}
              >
                <Item>
                  <CheckboxInner
                    readOnly={true}
                    isChecked={countriesFilter$.value.has(country_code)}
                    id={country_code}
                    name={country}
                    onChange={() => {}}
                    style={{ marginRight: '16px' }}
                  />
                  <ReactCountryFlag
                    countryCode={my_country_code}
                    svg
                    style={{
                      width: '20px',
                      height: '15px',
                      marginRight: '8px',
                    }}
                  />
                  <NameBox>
                    <ParagraphWithTooltip
                      text={` ${country} - ${country_code}`}
                      search={searchValue$.value}
                      isHighlight
                      highlightStyle={{ color: `${Palette.Primary._500}`, fontWeight: '500' }}
                    />
                  </NameBox>
                </Item>
              </MenuItem>
            );
          }}
        />
      </Content>
      <Footer>
        <Buttons.SecondarySmall
          id={resetBtnId}
          isDisabled={!countriesFilter$.value.size}
          onClick={resetClick}
        >
          Reset
        </Buttons.SecondarySmall>
        <Buttons.PrimarySmall
          id={applyBtnId}
          isDisabled={!changingFlag$.value}
          onClick={applyClick}
        >
          Apply
        </Buttons.PrimarySmall>
      </Footer>
    </Box>
  );
};

const NameBox = styled.div`
  width: 65%;
`;

const Item = styled.div`
  height: 48px;
  user-select: none;
  display: flex;
  align-items: center;
`;

const Footer = styled.div`
  display: flex;
  height: 64px;
  gap: 16px;
  justify-content: end;
  background: ${Palette.PrimaryGray._000};
  border-top: 1px solid ${Palette.PrimaryGray._200};
  border-bottom-right-radius: 4px;
  padding: 16px 24px;
`;

const Content = styled.ul`
  padding: 0;
  overflow-x: hidden;
  width: 226px;
  height: auto;
  max-height: 200px;
  overflow-y: auto;
  overscroll-behavior: none;
  ::-webkit-scrollbar {
    width: 6px;
  }
  ::-webkit-scrollbar-thumb {
    background-color: #cbd5e1;
    border-radius: 8px;
  }
`;

const Box = styled.div`
  width: 100%;
  height: auto;
`;

const SearchBox = styled.div`
  padding: 0 16px;
  border-bottom: 1px solid ${Palette.PrimaryGray._200};
  width: 100%;
`;
